import { useCallback, useEffect, useMemo, useState } from 'react'

import { Box, Card, Tabs } from '@mantine/core'
import { IconId, IconUsers } from '@tabler/icons-react'
import { Title as RATitle, useTranslate } from 'react-admin'
import { useNavigate, useParams } from 'react-router-dom'
import { useUserStore } from '../../stores/useUserStore'
import { Forbidden } from '../Forbidden'
import { RolesList } from './Roles/RolesList'
import { UsersList } from './UsersList'

type TabOption = 'users' | 'roles'

export const UsersRoles = () => {
  const translate = useTranslate()
  const navigate = useNavigate()
  const params = useParams()
  const userStore = useUserStore()

  const [tab, setTab] = useState<TabOption>((params.usersRolesTab as TabOption) ?? 'users')

  // biome-ignore lint/correctness/useExhaustiveDependencies: onload
  useEffect(() => {
    navigate(`/users-roles/${tab}`)
  }, [])

  const onTabChange = useCallback(
    (tabValue?: TabOption) => {
      if (tabValue) {
        setTab(tabValue)
        navigate(`/users-roles/${tabValue}`)
      }
    },
    [navigate],
  )

  const title = useMemo(() => {
    if (tab === 'users') {
      return translate('pages.users._title')
    }

    if (tab === 'roles') {
      return translate('pages.users.roles')
    }
  }, [tab, translate])

  return (
    <Box w="100%">
      <RATitle title={title} />
      <Card shadow="xs">
        <Tabs
          defaultValue="users"
          value={tab}
          onChange={v => onTabChange((v as TabOption) ?? undefined)}
        >
          <Tabs.List>
            <Tabs.Tab value="users" leftSection={<IconUsers size={12} />}>
              {translate('pages.users._title')}
            </Tabs.Tab>
            <Tabs.Tab value="roles" leftSection={<IconId size={12} />}>
              {translate('pages.users.roles')}
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="users">
            <UsersList />
          </Tabs.Panel>
          <Tabs.Panel value="roles">
            {userStore.isAuthorised('roles.read') ? (
              <RolesList />
            ) : (
              <Forbidden permission="roles.read" />
            )}
          </Tabs.Panel>
        </Tabs>
      </Card>
    </Box>
  )
}
